import React, { useState, useMemo } from 'react';
import { Typography, Tag, Spin, Input, message, Form, Button } from 'antd';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import legacyService from 'services/legacy.service';
import {
  checklistItemsAtom,
  additionalItemsExpandedAtom,
  programDetailsExpandedAtom,
  programDetailsAtom,
} from '../../../atoms';
import { SCROLLING_LAYOUT_CLASSNAME } from 'components/Layout/Layout';
import { ArrowRight, TimeFilled } from '@carbon/icons-react';
import Card from 'components/Card/Card';
import Html from 'Html';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';
import classNames from 'classnames';
import ChecklistUploadFile from './ChecklistUploadFile';
import PreviousSubmissions from './PreviousSubmissions';
import { DATE_TIME_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';

const { Text } = Typography;
const { TextArea } = Input;

const STATUS_CONFIG = {
  Requested: { color: 'orange', dateField: 'requestedDate', label: 'Last requested on' },
  Submitted: { color: 'blue', dateField: 'submittedDate', label: 'Last submitted on' },
  Approved: { color: 'green', dateField: 'approvedDate', label: 'Last approved on' },
};

const STATUS_ORDER = { Requested: 1, Submitted: 2, Approved: 3 };

const getChecklistItemSubtitle = (item) => {
  const date = item.lastModifiedDate;
  if (!date) return null;

  let label;
  if (!item.complete && !item.submitted) {
    label = 'Last requested on';
  } else if (!item.complete && item.submitted) {
    label = 'Last submitted on';
  } else if ((item.complete && !item.submitted) || item.status === 'Approved') {
    label = 'Last approved on';
  } else {
    return null;
  }

  return (
    <Text className="text-gray-500 text-sm">
      {label} {dayjs(date).format(DATE_TIME_FORMAT_SENTENCE)}
    </Text>
  );
};

export default function ChecklistItems({ applicationId, loading }) {
  const [items, setItems] = useAtom(checklistItemsAtom);
  const [programDetails] = useAtom(programDetailsAtom);
  const [submitting, setSubmitting] = useState(false);
  const cardRefs = React.useRef({});
  const [openCardIds, setOpenCardIds] = useState([]);

  const programDetailsAccepted = programDetails?.accepted ?? false;

  const actionableItems = useMemo(() => {
    const filtered = items?.filter((item) => item.actionType !== 'NoAction') || [];
    return filtered.sort((a, b) => (STATUS_ORDER[a.status] || 999) - (STATUS_ORDER[b.status] || 999));
  }, [items]);

  const completedCount = useMemo(
    () => actionableItems.filter((item) => item.status === 'Submitted' || item.status === 'Approved').length,
    [actionableItems],
  );

  if (loading) {
    return (
      <div className="mt-10 mb-6 flex justify-center">
        <Spin />
      </div>
    );
  }

  if (actionableItems.length === 0) {
    return (
      <div className="mt-10 mb-6">
        <Text className="text-gray-600">No items requested yet</Text>
      </div>
    );
  }

  const handleSubmit = async (values, itemId, form, isInfoRequest = false) => {
    try {
      setSubmitting(true);

      if (isInfoRequest) {
        // For InfoRequest, submit only the text response in the note field
        await legacyService.submitChecklistItem({
          appId: applicationId,
          itemId,
          file: null,
          note: values.response || null,
        });
      } else {
        // Upload each file separately
        for (const fileWrapper of values.files) {
          const file = fileWrapper.originFileObj || fileWrapper;
          await legacyService.submitChecklistItem({
            appId: applicationId,
            itemId,
            file,
            note: values.notes || null,
          });
        }
      }

      message.success(isInfoRequest ? 'Response submitted successfully' : 'Document uploaded successfully');

      // Refresh checklist items
      const updatedItems = await legacyService.getChecklistItems(applicationId);
      setItems(updatedItems || []);

      // Reset form and close all cards
      form.resetFields();
      setOpenCardIds([]);
    } catch (error) {
      console.error('Error uploading document:', error);
      message.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Checklist Items List */}
        {actionableItems.map((item, index) => {
          const isOpen = openCardIds.includes(item.id);
          const isGated = !programDetailsAccepted;

          return (
            <div ref={(el) => (cardRefs.current[index] = el)} key={index}>
              <Card
                collapsible
                defaultOpen={false}
                open={isOpen}
                onOpenChange={(open) => setOpenCardIds(open ? [item.id] : [])}
                size="small"
                title={isGated ? <Text className="!text-gray-600 !font-semibold">{item.category}</Text> : item.category}
                subtitle={getChecklistItemSubtitle(item)}
                extra={
                  isGated ? (
                    <span className="text-sm font-semibold text-gray-400 bg-gray-100 rounded-full px-4 py-1.5 whitespace-nowrap pointer-events-none">
                      Complete Program details first
                    </span>
                  ) : (
                    <Tag
                      color={STATUS_CONFIG[item.status]?.color || 'default'}
                      className="rounded-full px-4 py-1 m-0 font-bold my-0 md:my-5 border-none"
                    >
                      {item.status}
                    </Tag>
                  )
                }
                noBodyPadding
                stickyHeader
                className={classNames({
                  'ring-4 ring-[#e6f0ff] border-solid border-2 !border-[#5d82e4] rounded-2xl': isOpen && !isGated,
                })}
                styles={{
                  extra: {
                    pointerEvents: 'none',
                  },
                }}
              >
                {/* Upload Form - Shows inside card when selected */}
                <div className="upload-form-content mt-4" onClick={(e) => e.stopPropagation()}>
                  {isGated ? (
                    <GatedItemContent />
                  ) : (
                    <ChecklistItemUploadForm
                      item={item}
                      submitting={submitting}
                      onSubmit={handleSubmit}
                      applicationId={applicationId}
                      isOpen={isOpen}
                    />
                  )}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      <Text className="block mt-6 text-gray-500">
        {completedCount} of {actionableItems.length} items completed
      </Text>
    </>
  );
}

function GatedItemContent() {
  const [, setProgramDetailsExpanded] = useAtom(programDetailsExpandedAtom);

  const handleStartProgramDetails = () => {
    setProgramDetailsExpanded(true);
    setTimeout(() => {
      const el = document.getElementById('program-details-card');
      const container = document.querySelector(`div.${SCROLLING_LAYOUT_CLASSNAME}`);
      if (el && container) {
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 100;
        container.scrollTo({ top, behavior: 'smooth' });
      }
    }, 150);
  };

  return (
    <div className="p-4 pt-0">
      <div className="bg-gray-50 border border-solid border-gray-200 rounded-xl p-5">
        <Text className="text-gray-500 block mb-4">Available after Program details. Takes about 30 seconds</Text>

        <Button
          type="primary"
          onClick={handleStartProgramDetails}
          className="!bg-indigo-600 !border-indigo-600 hover:!bg-indigo-700 hover:!border-indigo-700"
          icon={<ArrowRight className="mt-[3px]" />}
        >
          Start with Program details
        </Button>
      </div>
    </div>
  );
}

// Upload form component for a selected checklist item
function ChecklistItemUploadForm({ item, submitting, onSubmit, applicationId, isOpen }) {
  const [form] = Form.useForm();
  const [, setAdditionalItemsExpanded] = useAtom(additionalItemsExpandedAtom);

  const handleFinish = (values) => {
    onSubmit(values, item.id, form, item.actionType === 'InfoRequest');
  };

  const handleUploadSomethingElse = () => {
    setAdditionalItemsExpanded(true);
    setTimeout(() => {
      document.getElementById('additional-items-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const isInfoRequest = item.actionType === 'InfoRequest';

  if (item.status === 'Approved') {
    return (
      <>
        <div className="p-6 pt-1">
          {item.instructions && (
            <RevealingLink
              text="See full requirement details"
              reveals={
                <div>
                  <Text className="font-semibold block mb-2">Requirements:</Text>
                  <Html html={item.instructions} />
                </div>
              }
            />
          )}

          <div className="mt-4 bg-gray-50 border border-solid border-gray-200 rounded-xl p-6">
            <Text className="text-gray-500 block mb-4">
              This item is approved. To share something new, use the section below
            </Text>

            <Button type="primary" onClick={handleUploadSomethingElse}>
              Upload something else
            </Button>
          </div>
        </div>
        <PreviousSubmissions applicationId={applicationId} itemId={item.id} isOpen={isOpen} />
      </>
    );
  }

  return (
    <Form form={form} onFinish={handleFinish} layout="vertical">
      <div className="p-6 pt-1">
        {/* Instructions Section */}
        {(item.quickTip || item.timingNote) && (
          <div className="mb-6">
            {item.quickTip && <Text className="font-bold">{item.quickTip}</Text>}

            {item.timingNote && (
              <div className="flex items-center gap-2 text-gray-600 mt-2">
                <TimeFilled size={16} className="flex-shrink-0 mt-[-1px]" />

                <Text className="text-sm">{item.timingNote}</Text>
              </div>
            )}
          </div>
        )}

        {isInfoRequest ? (
          // Info Request UI - Text input only
          <>
            {item.instructions && (
              <div className="mb-4">
                <Html html={item.instructions} />
              </div>
            )}
            <Form.Item
              name="response"
              rules={[{ required: true, message: 'Please provide a response' }]}
              className="mb-4"
            >
              <TextArea placeholder="Enter your response" rows={4} maxLength={500} showCount />
            </Form.Item>
          </>
        ) : (
          // Document Upload UI
          <>
            {/* Upload area */}
            <ChecklistUploadFile />

            {/* Additional notes textarea */}
            <Form.Item name="notes" label="Additional notes? (optional)" className="mb-4">
              <TextArea placeholder="" rows={4} maxLength={200} style={{ resize: 'none' }} showCount />
            </Form.Item>
          </>
        )}

        {!isInfoRequest && item.instructions && (
          <RevealingLink
            text="See full requirement details"
            className="mt-8"
            reveals={
              <div>
                <Text className="font-semibold block mb-2">Requirements:</Text>
                <Html html={item.instructions} />
              </div>
            }
          />
        )}

        {/* Submit button */}
        <Form.Item className="block mb-0 mt-4">
          <Button
            type="primary"
            size="large"
            block
            htmlType="submit"
            loading={submitting}
            className={classNames('!h-12 !text-base !font-semibold', {
              'mt-8': isInfoRequest,
            })}
          >
            {submitting ? 'Submitting...' : 'Submit to Child Care Aware'}
          </Button>
        </Form.Item>
      </div>

      {/* Previous Submissions Section */}
      <PreviousSubmissions applicationId={applicationId} itemId={item.id} isOpen={isOpen} />
    </Form>
  );
}
